/**
 * Supabase API Service
 * Replaces all FastAPI backend calls with direct Supabase queries
 */

import { supabase } from '@/supabaseClient';

// ============== AUTHENTICATION ==============

export const authService = {
    async signUp(email, password, userData = {}) {
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: userData
            }
        });
        if (error) throw error;
        
        // Create user profile
        if (data.user) {
            await supabase.from('users').upsert({
                id: data.user.id,
                email: data.user.email,
                name: userData.name || '',
                role: 'user',
                subscription_plan: 'trial',
                trial_status: 'trial',
                trial_started_at: new Date().toISOString(),
                trial_ends_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                quote_limit: 5,
                invoice_limit: 5
            });
        }
        
        return data;
    },

    async signIn(email, password) {
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });
        if (error) throw error;
        return data;
    },

    async signOut() {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
    },

    async getSession() {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        return data.session;
    },

    async getUser() {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;
        
        if (user) {
            // Get full profile from users table
            const { data: profile } = await supabase
                .from('users')
                .select('*')
                .eq('id', user.id)
                .single();
            
            return { ...user, ...profile };
        }
        return null;
    },

    onAuthStateChange(callback) {
        return supabase.auth.onAuthStateChange(callback);
    }
};

// ============== CLIENTS ==============

export const clientsService = {
    async getAll() {
        const { data, error } = await supabase
            .from('clients')
            .select('*')
            .order('created_at', { ascending: false });
        if (error) throw error;
        return data;
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('clients')
            .select('*')
            .eq('id', id)
            .single();
        if (error) throw error;
        return data;
    },

    async create(clientData) {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
            .from('clients')
            .insert({ ...clientData, user_id: user.id })
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async update(id, clientData) {
        const { data, error } = await supabase
            .from('clients')
            .update(clientData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('clients')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }
};

// ============== QUOTES ==============

export const quotesService = {
    async getAll(filters = {}) {
        let query = supabase
            .from('quotes')
            .select('*, clients(name, email)')
            .order('created_at', { ascending: false });
        
        if (filters.status) {
            query = query.eq('status', filters.status);
        }
        if (filters.client_id) {
            query = query.eq('client_id', filters.client_id);
        }
        
        const { data, error } = await query;
        if (error) throw error;
        return data;
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('quotes')
            .select('*, clients(*)')
            .eq('id', id)
            .single();
        if (error) throw error;
        return data;
    },

    async create(quoteData) {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Check trial limits
        const canCreate = await trialService.checkLimit('quote');
        if (!canCreate.allowed) {
            throw new Error(canCreate.message);
        }
        
        // Generate quote number
        const quoteNumber = await this.generateNumber();
        
        const { data, error } = await supabase
            .from('quotes')
            .insert({ 
                ...quoteData, 
                user_id: user.id,
                quote_number: quoteNumber
            })
            .select()
            .single();
        if (error) throw error;
        
        // Increment counter
        await trialService.incrementUsage('quotes_created');
        
        return data;
    },

    async update(id, quoteData) {
        const { data, error } = await supabase
            .from('quotes')
            .update(quoteData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('quotes')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    async generateNumber() {
        const year = new Date().getFullYear();
        const { data: { user } } = await supabase.auth.getUser();
        
        // Get or create counter
        const { data: counter } = await supabase
            .from('counters')
            .select('current_value')
            .eq('user_id', user.id)
            .eq('counter_type', 'quote')
            .eq('year', year)
            .single();
        
        const nextValue = (counter?.current_value || 0) + 1;
        
        await supabase
            .from('counters')
            .upsert({
                user_id: user.id,
                counter_type: 'quote',
                year: year,
                current_value: nextValue
            }, { onConflict: 'user_id,counter_type,year' });
        
        return `DEV-${year}-${String(nextValue).padStart(4, '0')}`;
    },

    async convertToInvoice(quoteId) {
        const quote = await this.getById(quoteId);
        if (!quote) throw new Error('Devis non trouvé');
        
        const invoiceData = {
            client_id: quote.client_id,
            client_name: quote.client_name,
            client_email: quote.client_email,
            client_address: quote.client_address,
            items: quote.items,
            total_ht: quote.total_ht,
            total_tva: quote.total_tva,
            total_ttc: quote.total_ttc,
            notes: quote.notes,
            quote_id: quoteId,
            payment_status: 'unpaid',
            due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        };
        
        const invoice = await invoicesService.create(invoiceData);
        
        // Update quote status
        await this.update(quoteId, { status: 'accepted' });
        
        return invoice;
    }
};

// ============== INVOICES ==============

export const invoicesService = {
    async getAll(filters = {}) {
        let query = supabase
            .from('invoices')
            .select('*, clients(name, email)')
            .order('created_at', { ascending: false });
        
        if (filters.payment_status) {
            query = query.eq('payment_status', filters.payment_status);
        }
        if (filters.client_id) {
            query = query.eq('client_id', filters.client_id);
        }
        
        const { data, error } = await query;
        if (error) throw error;
        return data;
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('invoices')
            .select('*, clients(*)')
            .eq('id', id)
            .single();
        if (error) throw error;
        return data;
    },

    async create(invoiceData) {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Check trial limits
        const canCreate = await trialService.checkLimit('invoice');
        if (!canCreate.allowed) {
            throw new Error(canCreate.message);
        }
        
        // Generate invoice number
        const invoiceNumber = await this.generateNumber();
        
        const { data, error } = await supabase
            .from('invoices')
            .insert({ 
                ...invoiceData, 
                user_id: user.id,
                invoice_number: invoiceNumber
            })
            .select()
            .single();
        if (error) throw error;
        
        // Increment counter
        await trialService.incrementUsage('invoices_created');
        
        return data;
    },

    async update(id, invoiceData) {
        const { data, error } = await supabase
            .from('invoices')
            .update(invoiceData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('invoices')
            .delete()
            .eq('id', id);
        if (error) throw error;
    },

    async generateNumber() {
        const year = new Date().getFullYear();
        const { data: { user } } = await supabase.auth.getUser();
        
        const { data: counter } = await supabase
            .from('counters')
            .select('current_value')
            .eq('user_id', user.id)
            .eq('counter_type', 'invoice')
            .eq('year', year)
            .single();
        
        const nextValue = (counter?.current_value || 0) + 1;
        
        await supabase
            .from('counters')
            .upsert({
                user_id: user.id,
                counter_type: 'invoice',
                year: year,
                current_value: nextValue
            }, { onConflict: 'user_id,counter_type,year' });
        
        return `FAC-${year}-${String(nextValue).padStart(4, '0')}`;
    },

    async markAsPaid(id, amount = null) {
        const invoice = await this.getById(id);
        const paidAmount = amount || invoice.total_ttc;
        
        const status = paidAmount >= invoice.total_ttc ? 'paid' : 'partial';
        
        return this.update(id, {
            payment_status: status,
            paid_amount: paidAmount,
            paid_at: new Date().toISOString()
        });
    }
};

// ============== SETTINGS ==============

export const settingsService = {
    async get() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;
        
        const { data, error } = await supabase
            .from('settings')
            .select('*')
            .eq('user_id', user.id)
            .single();
        
        // PGRST116 = no rows returned, not an error for us
        if (error && error.code !== 'PGRST116') throw error;
        return data;
    },

    async update(settingsData) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Map frontend field names to database column names
        const fieldMapping = {
            'address': 'company_address',
            'phone': 'company_phone',
            'email': 'company_email',
            'siret': 'company_siret',
            'vat_number': 'company_tva',
            'iban': 'bank_iban',
            'bic': 'bank_bic',
            'auto_entrepreneur_mention': 'legal_mentions',
            'default_payment_delay_days': 'default_payment_terms',
            'logo_base64': 'company_logo_url',
        };
        
        // Transform the data
        const mappedData = {};
        for (const [key, value] of Object.entries(settingsData)) {
            // Skip null/undefined values and special fields
            if (value === undefined || value === null) continue;
            if (key === 'logo_base64' && !value) continue;
            
            const dbField = fieldMapping[key] || key;
            mappedData[dbField] = value;
        }
        
        console.log('[Settings] Saving data:', mappedData);
        
        // First check if settings exist for this user
        const existing = await this.get();
        
        let result;
        if (existing?.id) {
            // Update existing settings
            const { data, error } = await supabase
                .from('settings')
                .update({ ...mappedData, updated_at: new Date().toISOString() })
                .eq('id', existing.id)
                .select()
                .single();
            if (error) {
                console.error('[Settings] Update error:', error);
                throw error;
            }
            result = data;
        } else {
            // Insert new settings
            const { data, error } = await supabase
                .from('settings')
                .insert({ 
                    ...mappedData, 
                    user_id: user.id,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                })
                .select()
                .single();
            if (error) {
                console.error('[Settings] Insert error:', error);
                throw error;
            }
            result = data;
        }
        
        return result;
    },

    async uploadLogo(file) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        // Clean filename - remove special characters
        const cleanName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `logos/${user.id}/${Date.now()}_${cleanName}`;
        
        // Upload file
        const { error: uploadError } = await supabase.storage
            .from('documents')
            .upload(fileName, file, { upsert: true });
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
            .from('documents')
            .getPublicUrl(fileName);
        
        // Save URL to settings
        await this.update({ company_logo_url: publicUrl });
        
        return publicUrl;
    }
};

// ============== TRIAL & SUBSCRIPTION ==============

export const trialService = {
    async getStatus() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;
        
        const { data: profile, error } = await supabase
            .from('users')
            .select('*')
            .eq('id', user.id)
            .single();
        
        // Si pas de profil, retourner des valeurs par défaut pour un nouvel utilisateur en trial
        if (!profile || error) {
            console.log('[trialService] Profil non trouvé, utilisation des valeurs par défaut trial');
            const defaultTrialEnds = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
            return {
                plan: 'trial',
                status: 'trial',
                is_trial: true,
                trial_expired: false,
                trial_ends_at: defaultTrialEnds.toISOString(),
                trial_days_remaining: 7,
                days_remaining: 7,
                quote_limit: 5,
                invoice_limit: 5,
                quotes_count: 0,
                invoices_count: 0,
                quotes_used: 0,
                invoices_used: 0,
                user_role: 'user',
                is_super_admin: false,
                subscription_active: false,
            };
        }
        
        const now = new Date();
        const trialEnds = profile.trial_ends_at ? new Date(profile.trial_ends_at) : new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        const daysRemaining = Math.max(0, Math.ceil((trialEnds - now) / (1000 * 60 * 60 * 24)));
        
        const trialExpired = profile.trial_ends_at ? now > trialEnds : false;

        // Déterminer is_trial: true si trial_status est 'trial' OU si pas de subscription_plan valide
        const isTrialStatus = profile.trial_status === 'trial' || (!profile.trial_status && profile.subscription_plan === 'trial');

        return {
            plan: profile.subscription_plan || 'trial',
            status: profile.trial_status || 'trial',
            is_trial: isTrialStatus,
            trial_expired: trialExpired,
            trial_ends_at: profile.trial_ends_at,
            trial_days_remaining: daysRemaining,
            days_remaining: daysRemaining,
            quote_limit: profile.quote_limit || 5,
            invoice_limit: profile.invoice_limit || 5,
            quotes_count: profile.quotes_created || 0,
            invoices_count: profile.invoices_created || 0,
            quotes_used: profile.quotes_created || 0,
            invoices_used: profile.invoices_created || 0,
            user_role: profile.role,
            is_super_admin: profile.role === 'super_admin',
            subscription_active: profile.subscription_status === 'active' && profile.subscription_plan !== 'trial',
        };
    },

    async checkLimit(resourceType) {
        const status = await this.getStatus();
        
        // Super admin has unlimited access
        if (status.is_super_admin) {
            return { allowed: true, message: 'OK' };
        }
        
        // Check if trial expired
        if (status.is_trial && status.days_remaining <= 0) {
            return { 
                allowed: false, 
                message: 'Votre période d\'essai est terminée. Passez à un plan payant pour continuer.'
            };
        }
        
        // Check limits
        if (resourceType === 'quote') {
            if (status.quotes_used >= status.quote_limit) {
                return {
                    allowed: false,
                    message: `Limite de devis atteinte (${status.quotes_used}/${status.quote_limit}). Passez à un plan supérieur.`
                };
            }
        } else if (resourceType === 'invoice') {
            if (status.invoices_used >= status.invoice_limit) {
                return {
                    allowed: false,
                    message: `Limite de factures atteinte (${status.invoices_used}/${status.invoice_limit}). Passez à un plan supérieur.`
                };
            }
        }
        
        return { allowed: true, message: 'OK' };
    },

    async incrementUsage(field) {
        const { data: { user } } = await supabase.auth.getUser();
        
        const { data: profile } = await supabase
            .from('users')
            .select(field)
            .eq('id', user.id)
            .single();
        
        await supabase
            .from('users')
            .update({ [field]: (profile[field] || 0) + 1 })
            .eq('id', user.id);
    }
};

// ============== DASHBOARD ==============

export const dashboardService = {
    async getStats() {
        const { data: { user } } = await supabase.auth.getUser();
        
        // Get counts
        const [clientsResult, quotesResult, invoicesResult] = await Promise.all([
            supabase.from('clients').select('id', { count: 'exact' }).eq('user_id', user.id),
            supabase.from('quotes').select('id', { count: 'exact' }).eq('user_id', user.id),
            supabase.from('invoices').select('id, total_ttc, paid_amount, payment_status', { count: 'exact' }).eq('user_id', user.id)
        ]);
        
        const invoices = invoicesResult.data || [];
        const paidInvoices = invoices.filter(i => i.payment_status === 'paid' || i.payment_status === 'paye');
        const unpaidInvoices = invoices.filter(i => i.payment_status === 'unpaid' || i.payment_status === 'impaye');
        
        const totalTurnover = paidInvoices.reduce((sum, i) => sum + (i.total_ttc || 0), 0);
        const unpaidAmount = unpaidInvoices.reduce((sum, i) => sum + ((i.total_ttc || 0) - (i.paid_amount || 0)), 0);
        
        return {
            total_clients: clientsResult.count || 0,
            total_quotes: quotesResult.count || 0,
            total_invoices: invoicesResult.count || 0,
            total_turnover: totalTurnover,
            unpaid_invoices_count: unpaidInvoices.length,
            unpaid_invoices_amount: unpaidAmount
        };
    },

    async getFinancialReport(period = 'year') {
        const { data: { user } } = await supabase.auth.getUser();
        
        let startDate = new Date();
        if (period === 'month') startDate.setMonth(startDate.getMonth() - 1);
        else if (period === 'quarter') startDate.setMonth(startDate.getMonth() - 3);
        else if (period === 'year') startDate.setFullYear(startDate.getFullYear() - 1);
        
        const { data: invoices } = await supabase
            .from('invoices')
            .select('*')
            .eq('user_id', user.id)
            .gte('created_at', startDate.toISOString());
        
        const total_revenue = invoices?.reduce((sum, i) => sum + (i.total_ttc || 0), 0) || 0;
        const total_paid = invoices?.reduce((sum, i) => sum + (i.paid_amount || 0), 0) || 0;
        
        return {
            total_revenue,
            total_paid,
            total_pending: total_revenue - total_paid,
            total_overdue: 0,
            collection_rate: total_revenue > 0 ? (total_paid / total_revenue * 100) : 0,
            invoices_by_status: {},
            monthly_revenue: [],
            recent_payments: []
        };
    }
};

// ============== PREDEFINED ITEMS & KITS ==============

export const predefinedItemsService = {
    async getCategories() {
        const { data, error } = await supabase
            .from('predefined_items')
            .select('category')
            .order('category');
        if (error) throw error;
        
        const categories = [...new Set(data?.map(d => d.category) || [])];
        return categories;
    },

    async getByCategory(category) {
        let query = supabase
            .from('predefined_items')
            .select('*')
            .order('description');
        
        if (category) {
            query = query.eq('category', category);
        }
        
        const { data, error } = await query;
        if (error) throw error;
        return data;
    },

    async create(itemData) {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
            .from('predefined_items')
            .insert({ ...itemData, user_id: user.id })
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async update(id, itemData) {
        const { data, error } = await supabase
            .from('predefined_items')
            .update(itemData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('predefined_items')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }
};

export const kitsService = {
    async getAll() {
        const { data, error } = await supabase
            .from('kits')
            .select('*')
            .order('name');
        if (error) throw error;
        return data;
    },

    async getById(id) {
        const { data, error } = await supabase
            .from('kits')
            .select('*')
            .eq('id', id)
            .single();
        if (error) throw error;
        return data;
    },

    async create(kitData) {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
            .from('kits')
            .insert({ ...kitData, user_id: user.id })
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async update(id, kitData) {
        const { data, error } = await supabase
            .from('kits')
            .update(kitData)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    },

    async delete(id) {
        const { error } = await supabase
            .from('kits')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }
};

// Export default
// ============== PRO SERVICES ==============

export const proServicesService = {
    async getCatalog() {
        // Get categories
        const { data: categories, error: catError } = await supabase
            .from('service_categories')
            .select('*')
            .order('sort_order');
        if (catError) throw catError;
        
        // Get all services
        const { data: services, error: svcError } = await supabase
            .from('services')
            .select('*')
            .eq('is_active', true)
            .order('sort_order');
        if (svcError) throw svcError;
        
        // Map services to categories
        return (categories || []).map(cat => ({
            ...cat,
            services: (services || []).filter(s => s.category_id === cat.id)
        }));
    },
    
    async getMyRequests() {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        
        const { data, error } = await supabase
            .from('service_requests')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });
        
        if (error && error.code !== 'PGRST116') throw error;
        return data || [];
    },
    
    async createRequest(requestData) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        
        const { data, error } = await supabase
            .from('service_requests')
            .insert({
                ...requestData,
                user_id: user.id,
                status: 'pending'
            })
            .select()
            .single();
        if (error) throw error;
        return data;
    }
};

export default {
    supabase,
    auth: authService,
    clients: clientsService,
    quotes: quotesService,
    invoices: invoicesService,
    settings: settingsService,
    trial: trialService,
    dashboard: dashboardService,
    predefinedItems: predefinedItemsService,
    kits: kitsService,
    proServices: proServicesService
};
